import { getAggregatePeriodLabel } from '@modules/common/utils';
import { useIsTimerangeControlEnabled } from '@modules/quantify/hooks/useIsTimerangeControlEnabled';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import {
  IconButton,
  Stack,
  StackProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { TimeUnit } from 'generated/graphql';

interface LineChartXAxisLabelProps {
  labelData: {
    primary: string;
    secondary?: string;
  };
  offset?: number;
  viewBox?: {
    x: number;
    y: number;
    width: number;
  };
  shouldShowTimeRangeControls?: boolean;
  onPrevClick?: () => void;
  onNextClick?: () => void;
  isNextDisabled?: boolean;
  aggregatePeriod?: TimeUnit;
  containerStackProps?: StackProps;
}

export function LineChartXAxisLabel({
  viewBox: { x, y, width } = { x: 0, y: 0, width: 100 },
  offset = 0,
  labelData,
  shouldShowTimeRangeControls = true,
  onPrevClick,
  onNextClick,
  isNextDisabled = false,
  aggregatePeriod,
  containerStackProps,
}: LineChartXAxisLabelProps) {
  const theme = useTheme();
  const isTimerangeControlEnabled = useIsTimerangeControlEnabled();

  if (!isTimerangeControlEnabled || !shouldShowTimeRangeControls) {
    return (
      <g transform={`translate(${x + width / 2},${y})`}>
        <Typography
          component="text"
          offset={offset}
          x={0}
          y={0}
          dy={50}
          fill={theme.palette.text.secondary}
          textAnchor="middle"
          variant="caption"
        >
          {labelData.primary}
        </Typography>
      </g>
    );
  }

  return (
    <foreignObject
      width={width}
      height="30px"
      transform={`translate(${x - offset},${y + 36})`}
    >
      <Stack
        position="fixed"
        width="100%"
        height="fit-content"
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        {...containerStackProps}
      >
        <Tooltip
          title={`View data for previous ${getAggregatePeriodLabel(
            aggregatePeriod,
          )}`}
        >
          <IconButton
            size="small"
            onClick={onPrevClick}
            sx={{
              height: 'fit-content',
              color: theme.palette.text.secondary,
            }}
            aria-label="trend-chart-prev-duration-button"
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography color={theme.palette.text.secondary} variant="caption">
            {labelData.primary}
          </Typography>
          {labelData.secondary && (
            <Typography color={theme.palette.text.primary} variant="caption">
              ({labelData.secondary})
            </Typography>
          )}
        </Stack>
        <Tooltip
          title={`View data for next ${getAggregatePeriodLabel(
            aggregatePeriod,
          )}`}
        >
          <Stack>
            <IconButton
              size="small"
              disabled={isNextDisabled}
              onClick={onNextClick}
              sx={{
                height: 'fit-content',
                color: theme.palette.text.secondary,
              }}
              aria-label="trend-chart-next-duration-button"
            >
              <ArrowForwardRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Tooltip>
      </Stack>
    </foreignObject>
  );
}
